import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { RotateCcw, Sparkles } from 'lucide-react';
import { useCallback } from 'react';

import { educationJourneyModalIdAtom } from '@/atoms/modalJourneyObservation';
import {
  devPacingExemptModalIdsAtom,
  pacedModalSeenCountAtom,
} from '@/atoms/modalPacing';
import {
  heidi2OnboardingDevLastBookDemoAtom,
  heidi2OnboardingDevLastOutcomeAtom,
  heidi2OnboardingDevOpenAtom,
} from '@/components/dev/heidi2-onboarding-dev-atoms';
import { unseeModalLocal } from '@/lib/modal-debugger';
import { heidi2OnboardingLaunchAtom } from '@heidi/agents';
import {
  HEIDI_2_EVIDENCE_TOUR_SPOTLIGHT_MODAL_ID,
  HEIDI_2_EXPLORE_LATER_BANNER_MODAL_ID,
  HEIDI_2_GET_STARTED_CHECKLIST_MODAL_ID,
} from '@heidi/agents/contracts';
import {
  heidi2EvidenceTourHeadingHomeAtom,
  heidi2EvidenceTourStepAtom,
  heidi2ExploreLaterPreviewAtom,
  heidi2ExploreLaterPreviewStartedAtAtom,
  heidi2ExploreLaterRestartAtom,
  heidi2RequestAccessModalOpenAtom,
} from '@heidi/agents/data';
import { Button } from '@heidi/design-system/button';
import { TypographyCaption, TypographyP2SemiBold } from '@heidi/design-system/typography';

interface Heidi2OnboardingDevSectionProps {
  /** Called after the user triggers an action that should close the dev-tools sheet. */
  onRequestClose?: () => void;
}

/**
 * Dev-tools shortcut for the Heidi 2.0 first-run tour. Nothing in the product
 * mounts the flow yet, so this is the only way to see it: it opens directly,
 * as many times as you like, and reports which exit the run took.
 */
export function Heidi2OnboardingDevSection({
  onRequestClose,
}: Readonly<Heidi2OnboardingDevSectionProps>) {
  const setIsOpen = useSetAtom(heidi2OnboardingDevOpenAtom);
  const lastOutcome = useAtomValue(heidi2OnboardingDevLastOutcomeAtom);
  const lastBookDemo = useAtomValue(heidi2OnboardingDevLastBookDemoAtom);

  const [preview, setPreview] = useAtom(heidi2ExploreLaterPreviewAtom);
  const setPreviewStartedAt = useSetAtom(heidi2ExploreLaterPreviewStartedAtAtom);
  const setLaunch = useSetAtom(heidi2OnboardingLaunchAtom);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    onRequestClose?.();
  }, [setIsOpen, onRequestClose]);

  const setTourStep = useSetAtom(heidi2EvidenceTourStepAtom);
  const setTourHeadingHome = useSetAtom(heidi2EvidenceTourHeadingHomeAtom);
  const setPacedModalSeenCount = useSetAtom(pacedModalSeenCountAtom);
  const setEducationJourney = useSetAtom(educationJourneyModalIdAtom);
  const setDevPacingExempt = useSetAtom(devPacingExemptModalIdsAtom);
  const setRestart = useSetAtom(heidi2ExploreLaterRestartAtom);

  // Every preview starts from scratch: seen state cleared, the tour back at
  // step 0 on the rail, the session's nudge slot freed, and each surface
  // remounted so a local close does not keep it hidden. Clearing seen state is
  // local only — a refetch restores the backend's — and a close during a
  // preview still persists for real.
  const startPreview = useCallback(
    (outcome: 'skipped' | 'completed') => {
      [
        HEIDI_2_EXPLORE_LATER_BANNER_MODAL_ID,
        HEIDI_2_EVIDENCE_TOUR_SPOTLIGHT_MODAL_ID,
        HEIDI_2_GET_STARTED_CHECKLIST_MODAL_ID,
      ].forEach(unseeModalLocal);
      setTourStep(0);
      setTourHeadingHome(false);
      setPacedModalSeenCount(0);
      // Free the page load's R5 journey so the replayed tour can show.
      setEducationJourney(null);
      // Pacing holds every nudge back for an account's first week; a preview
      // shows them regardless.
      setDevPacingExempt([HEIDI_2_EVIDENCE_TOUR_SPOTLIGHT_MODAL_ID]);
      setRestart((current) => current + 1);
      setPreview(outcome);
      setPreviewStartedAt(new Date().toISOString());
      onRequestClose?.();
    },
    [
      onRequestClose,
      setDevPacingExempt,
      setEducationJourney,
      setPacedModalSeenCount,
      setPreview,
      setPreviewStartedAt,
      setRestart,
      setTourHeadingHome,
      setTourStep,
    ]
  );

  const handleOpenTourAtHandoff = useCallback(() => {
    setLaunch({ startAt: 'handoff' });
    onRequestClose?.();
  }, [onRequestClose, setLaunch]);

  const setRequestAccessModalOpen = useSetAtom(heidi2RequestAccessModalOpenAtom);
  const handleOpenRequestAccessModal = useCallback(() => {
    setRequestAccessModalOpen(true);
    onRequestClose?.();
  }, [onRequestClose, setRequestAccessModalOpen]);

  return (
    <section aria-labelledby="dev-tools-heidi2-onboarding-heading" className="mb-6">
      <TypographyP2SemiBold id="dev-tools-heidi2-onboarding-heading" className="mb-2">
        Heidi II onboarding
      </TypographyP2SemiBold>
      <TypographyCaption className="mb-3 text-text-tertiary">
        Replays the Heidi 2.0 first-run tour: entry modal, then the built steps. Finishing
        the last step routes to Home, the way the real flow will; exiting early leaves you
        here. No analytics fire and no &quot;seen&quot; flag is written, so the outcome is
        reported below instead.{' '}
        <strong>See how it works really activates Heidi II</strong> on your account —
        leaving the tour does not undo it. The finale&apos;s{' '}
        <strong>Book a demo submits a Heidi 2 onboarding demo lead</strong> through
        sales-booking; the details are also reported below.
      </TypographyCaption>

      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-full"
        onClick={handleOpen}
        data-testid="dev-tools-heidi2-onboarding-open"
      >
        <Sparkles size={14} aria-hidden /> Open Heidi II onboarding
      </Button>

      <TypographyCaption className="mb-2 mt-4 block text-text-tertiary">
        The non-admin request-access modal. <strong>Send</strong> really notifies your
        workspace admins and marks the awareness banner as engaged.
      </TypographyCaption>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-full"
        onClick={handleOpenRequestAccessModal}
        data-testid="dev-tools-heidi2-open-request-access-modal"
      >
        Open request-access modal
      </Button>

      <TypographyCaption className="mb-2 mt-4 block text-text-tertiary">
        Explore-later nudges: the top banner, the Home dot, the Home tour (the Home rail,
        then Recents on Home; a finisher sees Recents only), and the Get started checklist
        on Home. A preview treats you as a Heidi II clinician who took that route, without
        touching your account. Closing a nudge during a preview <strong>does</strong>{' '}
        persist, and finishing the reopened tour records it as finished.
      </TypographyCaption>
      <div className="flex flex-col gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full"
          onClick={() => {
            startPreview('skipped');
          }}
          data-testid="dev-tools-heidi2-preview-skipped"
        >
          Preview after &quot;Explore later&quot;
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full"
          onClick={() => {
            startPreview('completed');
          }}
          data-testid="dev-tools-heidi2-preview-completed"
        >
          Preview after finishing the tour
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full"
          onClick={handleOpenTourAtHandoff}
          data-testid="dev-tools-heidi2-open-tour-at-handoff"
        >
          Open tour at hand-off
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full"
          onClick={() => {
            startPreview(preview ?? 'skipped');
          }}
          data-testid="dev-tools-heidi2-restart-nudges"
        >
          <RotateCcw size={14} aria-hidden /> Restart nudges
          {preview
            ? ` (${preview === 'skipped' ? 'Explore later' : 'finished tour'})`
            : ''}
        </Button>
        {preview ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="w-full"
            onClick={() => {
              setPreview(null);
              setDevPacingExempt([]);
            }}
            data-testid="dev-tools-heidi2-stop-preview"
          >
            Stop previewing ({preview})
          </Button>
        ) : null}
      </div>

      {lastOutcome ? (
        <TypographyCaption
          className="mt-2 block text-text-tertiary"
          data-testid="dev-tools-heidi2-onboarding-last-outcome"
        >
          Last run finished as <span className="font-mono">{lastOutcome}</span>.
        </TypographyCaption>
      ) : null}

      {lastBookDemo ? (
        <TypographyCaption
          className="mt-2 block text-text-tertiary"
          data-testid="dev-tools-heidi2-onboarding-last-book-demo"
        >
          Last demo request:{' '}
          <span className="font-mono">
            {lastBookDemo.firstName} {lastBookDemo.lastName} · {lastBookDemo.email}
          </span>
          .
        </TypographyCaption>
      ) : null}
    </section>
  );
}

/**
 * Always-mounted counterpart to {@link Heidi2OnboardingDevSection}. Rendered
 * once by `DevToolsSidebar` (outside the sheet) so the page takeover survives
 * the sheet closing. Renders nothing until the atom flips on.
 */
